/**
 * Wordings of «Учитель» v3 — family «goals» (docs/TEACHING-V3.md §5; pool specs: ../spec.ts).
 * Rules: no Latin, NO squares (the board shows them), Гамбитик speaks of himself as a boy, the child through {g:…},
 * no «молодец», no clock words; «вот эти клетки / этот фланг / сюда» only in pools with a board cue.
 *
 * `v3.goal.<key>` — the second sentence of an advice (theme.ts `themeLink`) said ONLY when the ADVISED MOVE ITSELF
 * achieves the goal (`goalDoneBy` on the advice's fenAfter). So a wording links this very move to the plan («Так слон
 * нацелится…», «Вот и удар…, который мы готовили», «Наш план — …», «Хотим…») and never says the goal is still to be
 * prepared («готовим», «скоро»): the move IS the step. It must not repeat the advice's idea tail («— займём центр.»,
 * «— расшатаем центр соперника.») nor the whole castling advice — it adds the plan. Short (≤ 10 words, most 5–9):
 * at stages 1–2 the whole turn is ≤ 16 words and the theme link is cut first. Order-neutral where the goal has two
 * parts (developCastle: the last step may be either castling or the last minor piece).
 * `v3.goalDone.<key>` — praise (≤ 10 words, past tense / result) right after the child's OWN move achieved it; no
 * «ты …л» without {g:…}. Varied shapes («Ура, …», «Задумка удалась: …», «{g:Сам…}», «[факт] — по плану!»), not
 * one «[факт] — всё по плану!» template.
 * The pools have no subject: the piece words are the goal's own (the card's bishop, knight, rook), no placeholders.
 * Stage words (§2.10): «слабая пешка» from 2, «развитие» from 2, «связка», «диагональ», «вертикаль», «фланг»,
 * «открытая линия» from 3 — before that «пешка у короля», «длинная дорога из угла в угол», «сторона ферзя / короля»,
 * «свободная дорога».
 *
 * What the praise assumes of core `goals.ts` achievedOn() (docs/TEACHING-V3.md §6.4), per key (checked 24.09):
 *  aimF7 — a bishop of ours attacks the pawn next to the enemy king (still there); holdCentre — our pawns stand on the
 *  card's squares (one pawn or two: the words are number-neutral); centreStrike / flankCentre / freeBishop /
 *  takeCentre / pawnStorm — our pawn stands on the card's square; hitCentre / fightCentre / chain — and hits an enemy
 *  pawn; bothKnights — both knights out, not on the edge; pinKnight — our bishop pins a knight to a queen, rook or
 *  king; bishopFirst — the bishop is out (the pawn that would shut it in is not played first); pawnFortress — the
 *  three-pawn triangle stands; knightJump — the knight stands on the central square; bishopAtKing — the bishop stands
 *  on the square whose diagonal looks at the king's side («сторона короля» = the kingside, true wherever the enemy
 *  king is; «домик» only as the plan's wish); openLines — a ROOK stands on a file WITHOUT OUR pawns (it may still hold
 *  an enemy pawn — so never «открытая линия занята» in the praise; the move may also be a pawn leaving the rook's
 *  file — so the praise states the result, «у ладьи свободная дорога», not «ладья вышла»); knightHoldsCentre — the
 *  knight stands next to the centre; pressKnight — our bishop attacks the enemy knight that guards an enemy centre
 *  pawn; rookCentre — the rook stands behind the centre; rookFile — the rook stands on the card's line (its own pawn
 *  may still be on it — no «давит» as a fact); developAll — no knight or bishop at home; castle — a castling move;
 *  developCastle — minor pieces out AND castled; pressCentrePawn — the enemy centre pawn is attacked by our piece(s)
 *  of the card (one knight is enough — the words are number-neutral); chaseBishop — our pawn attacks the enemy bishop
 *  (it has to leave — «гоним», not «прогнали»); longDiagonal — the bishop stands in the corner of the long diagonal;
 *  tradeForSpace — a piece was traded and we are not behind; holdSquare — the square is guarded by two of ours (two
 *  pawns, or a bishop and a knight); attackKing — our queen and rook both reach the enemy king's neighbourhood, or the
 *  pawn stands on its attacking square; queensidePawns — two pawns of the queen's side have advanced.
 */
import type { LessonWordings } from '../types.ts';

export const WORDINGS: LessonWordings = {
  // ─────────────── aimF7: the bishop aims at the weak pawn next to the enemy king (cue weak, line) ───────────────
  'v3.goal.aimF7': [
    { t: 'О пешке у короля соперника и был наш план.' },
    { t: 'Наш тайный план — пешка возле короля соперника.' },
    { t: 'Вот эта пешка рядом с королём — наша цель.' },
    { t: 'Хотим, чтобы слон смотрел на пешку у короля соперника.' },
    { t: 'Туда, к пешке у короля, и ведёт наш план.' },
    { t: 'Слабая пешка у короля — главная цель нашего плана.', stages: [2, 5] },
    { t: 'Слон против слабой пешки — так задумано с начала партии.', stages: [2, 5] },
  ],
  'v3.goalDone.aimF7': [
    { t: 'Слон нацелился на пешку у короля — всё по плану!' },
    { t: '{g:Сам навёл|Сама навела} слона на пешку у короля!' },
    { t: 'Пешке у короля соперника теперь неспокойно!' },
    { t: 'Слон вышел на охоту за пешкой у короля!' },
    { t: 'Вот и слабая пешка у короля на прицеле слона!', stages: [2, 5] },
    { t: 'Так и задумали: слон уже целится в слабую пешку!', stages: [2, 5] },
  ],

  // ─────────────── holdCentre: our pawns hold the centre (cue center) ───────────────
  'v3.goal.holdCentre': [
    { t: 'Пусть пешки держат центр, как крепкий забор.' },
    { t: 'Наш план — крепко держать центр.' },
    { t: 'С этим ходом центр будет таким, как мы задумали.' },
    { t: 'Держим вот эти клетки в центре — это наш план.' },
    { t: 'Главное сейчас — не отдать центр сопернику.' },
    { t: 'Крепкий центр из пешек — вот что мы задумали.' },
    { t: 'В центре мы хозяева — так задумано.' },
    { t: 'Сегодня центр держат наши пешки — так мы решили.' },
    { t: 'Центр держим пешками, а фигуры выйдут следом.' },
  ],
  'v3.goalDone.holdCentre': [
    { t: 'Центр стоит крепко — всё по плану!' },
    { t: 'Ура, центр под надёжной охраной пешек!' },
    { t: '{g:Сам укрепил|Сама укрепила} центр — как мы и задумали!' },
    { t: 'Теперь центр в надёжных руках — план сработал!' },
    { t: 'Задумка удалась: центр держится!' },
    { t: 'Держим центр крепко — как и хотели!' },
    { t: 'Я этого и ждал: в центре полный порядок!' },
  ],

  // ─────────────── centreStrike: the pawn strike at the centre — the advised move IS the strike (cue center) ───────────────
  'v3.goal.centreStrike': [
    { t: 'Вот и удар пешкой по центру, который мы готовили.' },
    { t: 'Этого удара пешкой мы и ждали по плану.' },
    { t: 'Пора ударить пешкой по центру — так задумано.' },
    { t: 'Готовили-готовили — и вот пешка идёт в бой!' },
    { t: 'Так пешка ударит по центру — это наш план.' },
    { t: 'Время пришло: пешка вступает в спор за центр.' },
    { t: 'Наш план такой: пешка смело шагает к центру.' },
    { t: 'Этим ходом начинается бой за центр — так мы и задумали.' },
    { t: 'Пешка выходит на удар — точно по плану.' },
  ],
  'v3.goalDone.centreStrike': [
    { t: 'Пешка ударила по центру — как мы и готовили!' },
    { t: 'Удар пешкой сделан — всё по плану!' },
    { t: '{g:Сам решился|Сама решилась} на удар пешкой — смело!' },
    { t: 'Ура, пешка вступила в бой за центр!' },
    { t: 'Этого удара я и ждал — точно по плану!' },
    { t: 'Теперь в центре жарко — план работает!' },
    { t: 'Бах — и в центре начался спор!' },
  ],

  // ─────────────── bothKnights: both knights come out toward the centre (cue center) ───────────────
  'v3.goal.bothKnights': [
    { t: 'Так оба коня будут в игре — это наш план.' },
    { t: 'Второй конь тоже скачет к центру — так задумано.' },
    { t: 'Кони любят центр, поэтому ведём туда обоих.' },
    { t: 'Два коня рядом с центром — наша цель.' },
    { t: 'Хотим, чтобы оба коня были поближе к центру.' },
    { t: 'Пусть кони скачут к центру парой — это наш план.' },
    { t: 'Оба коня в бой — вот и вся задумка!' },
  ],
  'v3.goalDone.bothKnights': [
    { t: 'Оба коня вышли — всё по плану!' },
    { t: 'Два коня в игре — как мы и задумали!' },
    { t: '{g:Сам вывел|Сама вывела} обоих коней — план выполнен!' },
    { t: 'Второй конь тоже прискакал — пара готова!' },
    { t: 'Цок-цок — оба коня уже у центра!' },
    { t: 'Оба коня проснулись — этого мы и хотели!' },
  ],

  // ─────────────── pinKnight: the bishop pins the enemy knight (cue line); «связка» from stage 3 ───────────────
  'v3.goal.pinKnight': [
    { t: 'Вот он, наш план: слон держит коня соперника.' },
    { t: 'Хотим, чтобы конь соперника боялся сдвинуться с места.' },
    { t: 'Так мы и задумали: слон против коня соперника.' },
    { t: 'По вот этой линии и работает наш план.' },
    { t: 'Коню соперника станет страшно уходить — так задумано.' },
    { t: 'Задумка такая: связать коня соперника слоном.', stages: [3, 5] },
    { t: 'Связка коня слоном — вот наш план.', stages: [3, 5] },
    { t: 'Связку мы и готовили с самого начала.', stages: [3, 5] },
  ],
  'v3.goalDone.pinKnight': [
    { t: 'Слон прижал коня соперника — всё по плану!' },
    { t: 'Теперь коню соперника страшно уходить — план сработал!' },
    { t: '{g:Сам нашёл|Сама нашла}, как прижать коня слоном!' },
    { t: 'Коню соперника теперь лучше стоять на месте — как задумано!' },
    { t: 'Связка готова — ровно как в нашем плане!', stages: [3, 5] },
    { t: 'Слон связал коня — как мы и задумали!', stages: [3, 5] },
  ],

  // ─────────────── bishopFirst: the bishop comes out before the pawn shuts its door (cue piece) ───────────────
  'v3.goal.bishopFirst': [
    { t: 'Слон выходит первым, пока пешка не загородила ему дорогу.' },
    { t: 'Наш план — вывести слона, пока путь свободен.' },
    { t: 'Выпускаем слона на волю, пока дверь открыта!' },
    { t: 'Хотим, чтобы слон не остался запертым за пешками.' },
    { t: 'Вот этого слона выводим первым — так задумано.' },
    { t: 'Сначала слон, а пешка закроет дверь уже за ним.' },
  ],
  'v3.goalDone.bishopFirst': [
    { t: 'Слон успел выйти раньше пешки — по плану!' },
    { t: 'Пока дверь была открыта, слон выскочил!' },
    { t: '{g:Сам выпустил|Сама выпустила} слона раньше пешки — как задумано!' },
    { t: 'Теперь и пешка может шагнуть — слон уже снаружи!' },
    { t: 'Слон вышел первым и не остался взаперти!' },
    { t: 'Вот и слон на свободе — всё по плану!' },
  ],

  // ─────────────── pawnFortress: a fortress of pawns in the centre (cue center) ───────────────
  'v3.goal.pawnFortress': [
    { t: 'Этим ходом достроим крепость из пешек.' },
    { t: 'Пешки встают рядом, как кирпичики нашей крепости.' },
    { t: 'За пешечной стеной нашим фигурам будет спокойно.' },
    { t: 'По плану пешки держатся вместе, плечом к плечу.' },
    { t: 'Так крепость из пешек будет готова — это наш план.' },
    { t: 'Центр укрепляем пешками, как настоящие строители.' },
    { t: 'Хотим прочный дом из пешек в центре.' },
  ],
  'v3.goalDone.pawnFortress': [
    { t: 'Крепость из пешек построена — всё по плану!' },
    { t: 'Пешки встали стеной — центр как крепость!' },
    { t: '{g:Сам достроил|Сама достроила} пешечную крепость — как задумано!' },
    { t: 'Последний кирпичик на месте — крепость готова!' },
    { t: 'Стена из пешек выросла — нашим фигурам теперь спокойно!' },
    { t: 'Ура, крепость в центре стоит!' },
  ],

  // ─────────────── knightJump: the knight jumps to a central square — the advised move IS the jump (cue center) ───────────────
  'v3.goal.knightJump': [
    { t: 'Наш план — прыгнуть конём в центр.' },
    { t: 'Вот и прыжок конём в центр, который мы задумали.' },
    { t: 'Так конь окажется в самой середине доски — по плану.' },
    { t: 'Из центра конь смотрит во все стороны — этого и хотим.' },
    { t: 'Цель плана — конь в центре доски.' },
    { t: 'Конь скачет в центр — ради этого всё и затевали.' },
    { t: 'Пора коню в центр — так задумано.' },
  ],
  'v3.goalDone.knightJump': [
    { t: 'Конь прыгнул в центр — как по плану!' },
    { t: 'Прыг-скок — и конь уже в центре!' },
    { t: '{g:Сам отправил|Сама отправила} коня в центр — план выполнен!' },
    { t: 'Конь в центре — я как раз этого и ждал!' },
    { t: 'Центр занят конём — всё по плану!' },
    { t: 'Ура, конь встал в самой середине доски!' },
  ],

  // ─────────────── bishopAtKing: the bishop looks at the king's side (cue line, king); «диагональ» from 3 ───────────────
  'v3.goal.bishopAtKing': [
    { t: 'Так слон будет смотреть на сторону короля соперника.' },
    { t: 'По плану слон смотрит туда, где обычно прячется король соперника.' },
    { t: 'Хотим, чтобы слон издалека целился в домик короля соперника.' },
    { t: 'Слон станет нашим разведчиком: он следит за стороной короля.' },
    { t: 'Наш план — нацелить слона на сторону короля соперника.' },
    { t: 'По вот этой линии слон смотрит в сторону короля соперника.' },
    { t: 'Слон встанет на диагональ в сторону короля соперника.', stages: [3, 5] },
  ],
  'v3.goalDone.bishopAtKing': [
    { t: 'Слон смотрит на сторону короля соперника — по плану!' },
    { t: 'Разведчик на посту: слон следит за стороной короля!' },
    { t: '{g:Сам поставил|Сама поставила} слона смотреть на сторону короля!' },
    { t: 'Теперь королю соперника неуютно: слон целится в его сторону!' },
    { t: 'Всё как задумали: слон на хорошем месте!' },
    { t: 'Слон встал на диагональ в сторону короля соперника — как хотели!', stages: [3, 5] },
  ],

  // ─────────────── fightCentre: we fight for the centre at once (cue center) ───────────────
  'v3.goal.fightCentre': [
    { t: 'Наш план — сразу спорить за центр.' },
    { t: 'Ждать не будем: пешка сразу встаёт в центр.' },
    { t: 'Сразу в бой за центр — это и есть задумка!' },
    { t: 'Центр не отдаём: спорим за него с первых ходов.' },
    { t: 'Хотим сразу потолкаться с соперником в центре.' },
    { t: 'Смело идём в центр — это наш план.' },
    { t: 'Вот за эти клетки и идёт спор — так задумано.' },
  ],
  'v3.goalDone.fightCentre': [
    { t: 'Пешка смело шагнула в центр — как по плану!' },
    { t: 'Спор за центр начался — всё по плану!' },
    { t: '{g:Сам начал|Сама начала} спор за центр — так и задумано!' },
    { t: 'Сразу в центр — именно этого мы и хотели!' },
    { t: 'Ура, в центре закипела борьба!' },
    { t: 'Толкаемся с соперником в центре — задумка удалась!' },
  ],

  // ─────────────── openLines: a rook gets a line without our pawns (cue line); «открытая линия» from 3 ───────────────
  'v3.goal.openLines': [
    { t: 'Наш план — дать фигурам свободные дороги.' },
    { t: 'По плану фигуры выходят туда, где им просторно.' },
    { t: 'Хотим, чтобы нашим фигурам было где разбежаться.' },
    { t: 'Так ладья получит свободную дорогу — это наш план.' },
    { t: 'Цель плана — простор для наших фигур.' },
    { t: 'Задумка такая: ставить фигуры на открытые линии.', stages: [3, 5] },
    { t: 'Ладьям нужны открытые линии — туда мы их и ведём.', stages: [3, 5] },
  ],
  'v3.goalDone.openLines': [
    { t: 'У ладьи теперь свободная дорога — по плану!' },
    { t: 'Простор для фигуры найден — как задумано!' },
    { t: '{g:Сам нашёл|Сама нашла} фигуре свободную дорогу!' },
    { t: 'Теперь фигуре есть где разгуляться — так мы и хотели!' },
    { t: 'Своих пешек на линии нет — ладья свободна!' },
    { t: 'Ладья на вертикали без своих пешек — по плану!', stages: [3, 5] },
  ],

  // ─────────────── knightHoldsCentre: the knight guards the centre (cue center) ───────────────
  'v3.goal.knightHoldsCentre': [
    { t: 'Наш план — держать центр конём.' },
    { t: 'Так конь встанет сторожем у центра — как задумано.' },
    { t: 'Хотим, чтобы конь крепко держал центр.' },
    { t: 'Цель плана — конь, который охраняет центр.' },
    { t: 'Пусть конь присмотрит за центром — это часть нашего плана.' },
    { t: 'Вот эти клетки в центре и будет стеречь конь.' },
    { t: 'Конь рядом с центром — этого мы и добиваемся.' },
  ],
  'v3.goalDone.knightHoldsCentre': [
    { t: 'Конь встал на пост и держит центр — по плану!' },
    { t: 'Сторож центра на месте — как задумано!' },
    { t: '{g:Сам поставил|Сама поставила} коня сторожить центр!' },
    { t: 'Центр под охраной коня — этого мы и хотели!' },
    { t: 'Конь занял место рядом с центром — план сработал!' },
    { t: 'Ура, конь присматривает за центром!' },
  ],

  // ─────────────── pressKnight: the bishop presses the knight that guards the centre (cue line) ───────────────
  'v3.goal.pressKnight': [
    { t: 'Наш план — давить слоном на коня, который защищает центр.' },
    { t: 'Так слон пристанет к коню соперника — по плану.' },
    { t: 'Хотим, чтобы коню соперника стало неуютно под прицелом.' },
    { t: 'Конь соперника сторожит центр, а наш слон ему мешает.' },
    { t: 'Цель плана — слон против коня, который держит центр.' },
    { t: 'По вот этой линии слон и надавит на коня.' },
  ],
  'v3.goalDone.pressKnight': [
    { t: 'Слон надавил на коня соперника — как и задумывали!' },
    { t: 'Коню соперника стало неуютно — как мы и хотели!' },
    { t: '{g:Сам прижал|Сама прижала} коня соперника слоном!' },
    { t: 'Ура, сторож центра попал под прицел слона!' },
    { t: 'Слон нацелился на коня, который держит центр!' },
    { t: 'Задумка удалась: слон мешает коню сторожить центр!' },
  ],

  // ─────────────── rookCentre: the rook helps the centre from behind (cue line, center) ───────────────
  'v3.goal.rookCentre': [
    { t: 'Сегодня даже ладья работает на центр — так мы решили.' },
    { t: 'Так ладья встанет за центром и поможет ему.' },
    { t: 'Ладья тоже хочет помогать центру — так задумано.' },
    { t: 'Хотим, чтобы ладья поддержала центр издалека.' },
    { t: 'Цель плана — ладья за спиной у центра.' },
    { t: 'Вот эта линия ведёт в центр — там будет работать ладья.' },
    { t: 'Ладья — сильная помощница, пусть смотрит в центр.' },
  ],
  'v3.goalDone.rookCentre': [
    { t: 'Ладья встала в помощь центру — по плану!' },
    { t: 'Центр получил помощника — ладью!' },
    { t: '{g:Сам позвал|Сама позвала} ладью на помощь центру!' },
    { t: 'Ладья заняла место за центром — как задумано!' },
    { t: 'Теперь и ладья помогает центру — план сработал!' },
    { t: 'Ура, ладья тоже в деле!' },
  ],

  // ─────────────── hitCentre: a pawn strikes the opponent's centre (cue center) ───────────────
  'v3.goal.hitCentre': [
    { t: 'Наш план — ударить пешкой по центру соперника.' },
    { t: 'Так пешка нападёт на центр соперника — по плану.' },
    { t: 'Центр соперника — наша цель, ударим по нему пешкой.' },
    { t: 'Этот удар пешкой мы и задумали сегодня.' },
    { t: 'Об этом ударе мы и думали с начала партии.' },
    { t: 'Центр соперника пора потревожить — так задумано.' },
    { t: 'Вот эти клетки в центре — за них и поспорим.' },
  ],
  'v3.goalDone.hitCentre': [
    { t: 'Пешка ударила по центру соперника — по плану!' },
    { t: 'Ура, центр соперника зашатался!' },
    { t: '{g:Сам ударил|Сама ударила} пешкой по центру соперника!' },
    { t: 'Удар по центру получился — именно так мы и хотели!' },
    { t: 'Смелый удар пешкой — задумка удалась!' },
    { t: 'Теперь сопернику надо думать о своём центре — план сработал!' },
  ],

  // ─────────────── rookFile: the rook presses along its line (cue line); «вертикаль», «фланг» from 3 ───────────────
  'v3.goal.rookFile': [
    { t: 'Наш план — давить ладьёй по линии на стороне ферзя.' },
    { t: 'Этим ходом ладья займёт линию из нашего плана.' },
    { t: 'Прямая дорога для ладьи — вот что мы задумали.' },
    { t: 'Хотим, чтобы ладья давила по линии до лагеря соперника.' },
    { t: 'По этой линии и будет давить наша ладья.' },
    { t: 'Ладья выходит на свою дорогу — так задумано.' },
    { t: 'Давим ладьёй по вертикали на ферзевом фланге — так задумано.', stages: [3, 5] },
  ],
  'v3.goalDone.rookFile': [
    { t: 'Ладья встала на нужную линию — по плану!' },
    { t: 'Прямая дорога для ладьи найдена — как задумано!' },
    { t: '{g:Сам поставил|Сама поставила} ладью на её дорогу!' },
    { t: 'Ладья на месте — теперь ей есть куда давить!' },
    { t: 'План сработал: ладья на своей линии!' },
    { t: 'Вертикаль занята ладьёй — всё по плану!', stages: [3, 5] },
  ],

  // ─────────────── takeCentre: a pawn takes a central square (cue center) ───────────────
  'v3.goal.takeCentre': [
    { t: 'Это и есть наш план: пешка в центре.' },
    { t: 'По плану пешка занимает центр.' },
    { t: 'Хотим занять центр пешкой — это главные клетки доски.' },
    { t: 'Пешке в центре самое место — так задумано.' },
    { t: 'Вот эти клетки — центр, одну из них займёт наша пешка.' },
    { t: 'С пешки в центре и начинается наш план.' },
    { t: 'Сначала центр — так мы задумали.' },
  ],
  'v3.goalDone.takeCentre': [
    { t: 'Пешка в центре — всё по плану!' },
    { t: 'Задумка удалась: пешка заняла центр!' },
    { t: '{g:Сам вспомнил|Сама вспомнила} план: пешка в центре!' },
    { t: 'Наша пешка шагнула в центр — план выполнен!' },
    { t: 'Пешка заняла одну из главных клеток доски!' },
    { t: 'Теперь в центре наша пешка — так и задумали!' },
  ],

  // ─────────────── developAll: every knight and bishop into the game (no cue: no pointing); «развитие» from 2 ───────────────
  'v3.goal.developAll': [
    { t: 'Так все кони и слоны будут в игре — по плану.' },
    { t: 'По плану будим фигуры одну за другой.' },
    { t: 'Наш план — ни одного коня или слона дома.' },
    { t: 'Все фигуры — в игру, так задумано.' },
    { t: 'Дома никто не засиживается — вот наш план.' },
    { t: 'Скорее будим всех: и коней, и слонов.' },
    { t: 'Быстрое развитие — вот что нам сейчас нужно.', stages: [2, 5] },
  ],
  'v3.goalDone.developAll': [
    { t: 'Все кони и слоны вышли — всё по плану!' },
    { t: 'Фигуры проснулись и вышли в игру — как задумано!' },
    { t: '{g:Сам разбудил|Сама разбудила} коней и слонов — по плану!' },
    { t: 'Кони и слоны больше не спят дома — план выполнен!' },
    { t: 'Ура, дома не осталось ни коней, ни слонов!' },
    { t: 'Развитие идёт по плану — кони и слоны вышли!', stages: [2, 5] },
  ],

  // ─────────────── castle: the king hides by castling — follows the castling advice, adds the plan (cue king) ───────────────
  'v3.goal.castle': [
    { t: 'Об этом и был наш план с самого начала.' },
    { t: 'Королю пора в домик — так задумано.' },
    { t: 'Рокировка — важная часть нашего плана.' },
    { t: 'Король в крепости — вот что мы задумали.' },
    { t: 'Так король будет в безопасности, как мы и хотели.' },
    { t: 'Наш план — спрятать короля рокировкой.' },
    { t: 'Сегодня мы и хотели пораньше спрятать короля.' },
    { t: 'Ради этой рокировки мы и выводили фигуры.' },
  ],
  'v3.goalDone.castle': [
    { t: 'Король в домике — точно по нашему плану!' },
    { t: 'Король спрятался в домик — этого мы и хотели!' },
    { t: '{g:Сам спрятал|Сама спрятала} короля — как мы задумали!' },
    { t: 'Задумка удалась: король укрылся в домике!' },
    { t: 'Мы об этом и мечтали: король в укрытии!' },
    { t: 'Спрятали короля — я этого и ждал!' },
  ],

  // ─────────────── pressCentrePawn: our pieces press the enemy centre pawn (cue center) ───────────────
  'v3.goal.pressCentrePawn': [
    { t: 'Наш план — давить на пешку соперника в центре.' },
    { t: 'Так мы нацелимся на пешку соперника в центре.' },
    { t: 'Пешка соперника в центре — вот наша мишень.' },
    { t: 'Хотим, чтобы пешке соперника в центре стало жарко.' },
    { t: 'Не дадим покоя пешке в центре — так задумано.' },
    { t: 'Собираем фигуры против пешки соперника в центре.' },
  ],
  'v3.goalDone.pressCentrePawn': [
    { t: 'Надавили на пешку в центре — всё по плану!' },
    { t: 'Пешке соперника в центре стало жарко — как задумано!' },
    { t: '{g:Сам нашёл|Сама нашла}, как напасть на пешку в центре!' },
    { t: 'Пешка соперника в центре под прицелом — так мы и хотели!' },
    { t: 'Задумка удалась: пешке в центре неуютно!' },
    { t: 'Центральная пешка соперника попала под обстрел!' },
  ],

  // ─────────────── chaseBishop: our pawn chases the enemy bishop away (cue piece) ───────────────
  'v3.goal.chaseBishop': [
    { t: 'Наш план — прогнать слона соперника пешками.' },
    { t: 'Так и задумано: пешка против слона соперника.' },
    { t: 'Слон соперника нам мешает — прогоним его!' },
    { t: 'Хотим, чтобы слон соперника убежал подальше.' },
    { t: 'Вот этого слона и прогоним пешкой — это наша задумка.' },
    { t: 'Пусть слон соперника ищет новое место — этого мы и хотим.' },
    { t: 'Прогоним слона соперника с хорошего места — это наш план.' },
  ],
  'v3.goalDone.chaseBishop': [
    { t: 'Гоним слона соперника — всё по плану!' },
    { t: 'Слону соперника пора уходить — как задумано!' },
    { t: '{g:Сам напал|Сама напала} на слона соперника — пусть уходит!' },
    { t: 'Ура, слон соперника под ударом пешки!' },
    { t: 'Спугнули слона соперника — этого мы и хотели!' },
    { t: 'Слона соперника прогоняем — план работает!' },
  ],

  // ─────────────── chain: a pawn strikes the enemy pawn chain from the side (cue center) ───────────────
  'v3.goal.chain': [
    { t: 'Наш план — ударить по цепочке пешек соперника.' },
    { t: 'По плану пешка ударит по цепочке сбоку.' },
    { t: 'Цепочка пешек соперника — наша цель.' },
    { t: 'Хотим сломать цепочку пешек соперника.' },
    { t: 'Цепочку пешек соперника расшатаем ударом сбоку.' },
    { t: 'Пешки соперника стоят цепочкой, а мы ударим по ней.' },
    { t: 'Удар по цепочке — наша задумка на эту партию.' },
  ],
  'v3.goalDone.chain': [
    { t: 'Пешка ударила по цепочке соперника — по плану!' },
    { t: 'Цепочка соперника затрещала — как задумано!' },
    { t: '{g:Сам нашёл|Сама нашла} удар по цепочке пешек!' },
    { t: 'Ура, в цепочке соперника появилась трещина!' },
    { t: 'Цепочка пешек соперника под ударом — план работает!' },
    { t: 'Наша пешка напала на цепочку сбоку — как и хотели!' },
  ],

  // ─────────────── flankCentre: we fight for the centre with a pawn from the side (cue center) ───────────────
  'v3.goal.flankCentre': [
    { t: 'Наш план — спорить за центр сбоку.' },
    { t: 'По плану пешка сбоку спорит за центр.' },
    { t: 'Не лезем в центр напрямую, а спорим за него сбоку.' },
    { t: 'Пешка сбоку тоже смотрит в центр — так задумано.' },
    { t: 'Сбоку — тоже путь к центру, и мы по нему идём.' },
    { t: 'За эти клетки в центре мы поспорим сбоку.' },
    { t: 'Центр можно держать и сбоку — это наш план.' },
  ],
  'v3.goalDone.flankCentre': [
    { t: 'Пешка сбоку вступила в спор за центр — по плану!' },
    { t: 'Спорим за центр сбоку — как и задумали!' },
    { t: '{g:Сам нашёл|Сама нашла} путь к центру сбоку!' },
    { t: 'Удар сбоку по центру получился — план работает!' },
    { t: 'Хитро: пешка сбоку, а смотрит в центр!' },
    { t: 'Задумка удалась: центр под присмотром пешки!' },
  ],

  // ─────────────── longDiagonal: the bishop on the long diagonal (cue line); «диагональ» from 3 ───────────────
  'v3.goal.longDiagonal': [
    { t: 'Так слон встанет на самую длинную дорогу — по плану.' },
    { t: 'Слону нужна дорога через всю доску — так задумано.' },
    { t: 'Хотим, чтобы слон стрелял издалека по длинной дороге.' },
    { t: 'Слон любит длинные дороги — дадим ему длинную-предлинную.' },
    { t: 'Вот эта линия — длинная дорога для нашего слона.' },
    { t: 'Слон в уголке, а дорога у него длинная — так задумано.' },
    { t: 'Задумка простая: слон на длинной диагонали.', stages: [3, 5] },
    { t: 'Длинная диагональ идёт через центр — её и займёт слон.', stages: [3, 5] },
  ],
  'v3.goalDone.longDiagonal': [
    { t: 'Слон встал на длинную дорогу — по плану!' },
    { t: 'Ура, у слона самая длинная дорога на доске!' },
    { t: '{g:Сам вывел|Сама вывела} слона на длинную дорогу — как задумано!' },
    { t: 'Длинная дорога занята слоном — этого мы и хотели!' },
    { t: 'Длинная диагональ досталась слону — план выполнен!', stages: [3, 5] },
    { t: 'Диагональ из угла в угол теперь у слона!', stages: [3, 5] },
  ],

  // ─────────────── tradeForSpace: trades give our pieces room (cue capture) ───────────────
  'v3.goal.tradeForSpace': [
    { t: 'Наш план — меняться фигурами, чтобы стало просторнее.' },
    { t: 'Так мы разменяемся, и фигурам станет свободнее.' },
    { t: 'Размен нам поможет: места станет больше.' },
    { t: 'Хотим разменяться, чтобы фигурам не было тесно.' },
    { t: 'Меньше фигур — больше места, так задумано.' },
    { t: 'Когда тесно, помогает размен — это наш план.' },
  ],
  'v3.goalDone.tradeForSpace': [
    { t: 'Разменялись — и стало просторнее, по плану!' },
    { t: 'Ура, в нашем лагере стало просторнее!' },
    { t: '{g:Сам разменял|Сама разменяла} фигуры — места стало больше!' },
    { t: 'Размен сделан — нашим фигурам есть где развернуться!' },
    { t: 'Одной фигурой меньше — нашим легче дышать!' },
    { t: 'Просторно стало — значит, план работает!' },
  ],

  // ─────────────── freeBishop: a pawn strike frees the locked bishop (cue line) ───────────────
  'v3.goal.freeBishop': [
    { t: 'Задумали мы вот что: пешка выпустит слона на свободу.' },
    { t: 'По плану пешка шагнёт и откроет дорогу нашему слону.' },
    { t: 'Слон сидит взаперти — хотим его освободить.' },
    { t: 'Хотим открыть слону дверь, а пешка нам поможет.' },
    { t: 'Вот эта линия нужна слону — освободим её пешкой.' },
    { t: 'Запертый слон скучает — выпустим его, это наш план.' },
    { t: 'Пешка уступит слону дорогу — так задумано.' },
  ],
  'v3.goalDone.freeBishop': [
    { t: 'Удар пешкой сделан — слон скоро выйдет на свободу!' },
    { t: 'Дверь для слона открывается — всё по плану!' },
    { t: '{g:Сам сделал|Сама сделала} удар, который освобождает слона!' },
    { t: 'Пешка ударила, и слону стало свободнее!' },
    { t: 'Слон почти на свободе — план работает!' },
    { t: 'Шаг пешки сделан — теперь слону будет где погулять!' },
  ],

  // ─────────────── holdSquare: two of ours guard one central square (cue center — four squares: no pointing) ───────────────
  'v3.goal.holdSquare': [
    { t: 'Наш план — держать важную клетку в центре.' },
    { t: 'По плану не пускаем соперника на важную клетку в центре.' },
    { t: 'Одну клетку в центре держим особенно крепко.' },
    { t: 'Хотим, чтобы соперник не занял нашу клетку в центре.' },
    { t: 'Сторожим клетку в центре — так задумано.' },
    { t: 'Так важная клетка в центре будет под охраной.' },
    { t: 'Там, в центре, есть клетка, которую мы не отдадим.' },
  ],
  'v3.goalDone.holdSquare': [
    { t: 'Клетка в центре взята под охрану — по плану!' },
    { t: 'Теперь сопернику трудно занять важную клетку в центре!' },
    { t: '{g:Сам поставил|Сама поставила} сторожей у важной клетки!' },
    { t: 'Два сторожа у клетки в центре — как хотели!' },
    { t: 'Центральная клетка держится крепко — план работает!' },
    { t: 'Держим клетку в центре — как и задумали!' },
  ],

  // ─────────────── attackKing: an attack on the enemy king (cue flank, king — the flank is not drawn: no pointing); «фланг» from 3 ───────────────
  'v3.goal.attackKing': [
    { t: 'Наш план — атаковать короля соперника.' },
    { t: 'По плану фигуры собираются в поход на короля.' },
    { t: 'Хотим, чтобы королю соперника стало неуютно.' },
    { t: 'Все дороги ведут к королю соперника — так задумано.' },
    { t: 'Этим ходом атака на короля и начнётся.' },
    { t: 'Собираем силы для атаки на короля соперника.' },
    { t: 'Атака на королевском фланге — вот наш план.', stages: [3, 5] },
  ],
  'v3.goalDone.attackKing': [
    { t: 'Атака на короля началась — по плану!' },
    { t: 'Мы подобрались ближе к королю соперника — как задумано!' },
    { t: '{g:Сам повёл|Сама повела} атаку на короля — так мы и хотели!' },
    { t: 'Королю соперника стало тревожно — план работает!' },
    { t: 'Вперёд, на короля — задумка удалась!' },
    { t: 'Атака на королевском фланге пошла — всё по плану!', stages: [3, 5] },
  ],

  // ─────────────── developCastle: minor pieces out AND the king castled — the last step may be either (cue king); «развитие» from 2 ───────────────
  'v3.goal.developCastle': [
    { t: 'Так фигуры будут в игре, а король в домике.' },
    { t: 'Фигуры — в бой, король — в домик: так задумано.' },
    { t: 'Хотим поскорее разбудить фигуры и увести короля в домик.' },
    { t: 'Два дела у нас: разбудить фигуры и спрятать короля.' },
    { t: 'Последний шаг плана: фигуры в игре, король в домике.' },
    { t: 'Развитие и рокировка — вот наш план.', stages: [2, 5] },
  ],
  'v3.goalDone.developCastle': [
    { t: 'Фигуры вышли, король в домике — всё по плану!' },
    { t: 'Оба дела сделаны: фигуры в игре, король спрятан!' },
    { t: '{g:Сам успел|Сама успела} и фигуры вывести, и короля спрятать!' },
    { t: 'Задумка удалась: кони и слоны вышли, король укрылся!' },
    { t: 'Вот теперь порядок: фигуры в бою, король в домике!' },
    { t: 'Развитие закончено, рокировка сделана — по плану!', stages: [2, 5] },
  ],

  // ─────────────── queensidePawns: the pawns on the queen's side go forward (cue flank: no pointing); «фланг» from 3 ───────────────
  'v3.goal.queensidePawns': [
    { t: 'Пешки на стороне ферзя — наши главные бойцы сегодня.' },
    { t: 'По плану пешки на стороне ферзя идут в наступление.' },
    { t: 'Пешка за пешкой — так и наступаем по плану.' },
    { t: 'Цель плана — наступать пешками на стороне ферзя.' },
    { t: 'Об этом наступлении пешек мы и мечтали.' },
    { t: 'Пешки ферзевого фланга идут вперёд — это наш план.', stages: [3, 5] },
    { t: 'Наступление на ферзевом фланге — главное в нашем плане.', stages: [3, 5] },
  ],
  'v3.goalDone.queensidePawns': [
    { t: 'Пешки на стороне ферзя пошли вперёд — по плану!' },
    { t: 'Наступление на стороне ферзя началось — как задумано!' },
    { t: '{g:Сам двинул|Сама двинула} пешку вперёд на стороне ферзя!' },
    { t: 'Пешки шагают вперёд — задумка удалась!' },
    { t: 'Места на стороне ферзя стало больше — план работает!' },
    { t: 'Ферзевый фланг ожил — всё по плану!', stages: [3, 5] },
  ],

  // ─────────────── pawnStorm: a pawn on the king's side rushes forward — the advised move IS the push (cue flank: no pointing); «фланг» from 3 ───────────────
  'v3.goal.pawnStorm': [
    { t: 'Наш план — рывок пешкой вперёд.' },
    { t: 'По плану пешка на стороне короля рванёт вперёд.' },
    { t: 'Хотим, чтобы пешка пошла вперёд и потеснила соперника.' },
    { t: 'Сигнал дан: пешка рвётся вперёд, как задумано.' },
    { t: 'Рывок пешкой — ради этого мы и готовились.' },
    { t: 'Пешка пойдёт на штурм — так мы задумали.' },
    { t: 'Рывок пешкой на королевском фланге — вот наш план.', stages: [3, 5] },
  ],
  'v3.goalDone.pawnStorm': [
    { t: 'Пешка рванула вперёд — по плану!' },
    { t: 'Рывок пешкой сделан — как задумано!' },
    { t: '{g:Сам толкнул|Сама толкнула} пешку вперёд — так мы и хотели!' },
    { t: 'Ура, пешка смело бежит вперёд!' },
    { t: 'Рывок начался — пешка теснит соперника!' },
    { t: 'Пешка на королевском фланге пошла вперёд — план работает!', stages: [3, 5] },
  ],
};
